import React, { useRef, useEffect, useState, useCallback } from 'react';
import { WheelEntry } from '../../types';
import { getSegmentColor, getContrastTextColor, adjustBrightness } from '../../utils/colors';
import { degToRad } from '../../utils/wheelMath';

const WHEEL_LABEL_FONT = '"Plus Jakarta Sans", "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const CENTER_BUTTON_FONT = '"Outfit", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

interface SpinWheelCanvasProps {
  entries: WheelEntry[];
  rotationDeg: number;
  paletteId?: string;
  isSpinning?: boolean;
  onCenterClick?: () => void;
  disabled?: boolean;
  wheelNameFontSize?: number;
}

export const SpinWheelCanvas: React.FC<SpinWheelCanvasProps> = ({
  entries,
  rotationDeg,
  paletteId = 'vibrant',
  isSpinning = false,
  onCenterClick,
  disabled = false,
  wheelNameFontSize = 24,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [dimension, setDimension] = useState<number>(540);

  // Resize handler for high-DPI responsive canvas
  const updateDimensions = useCallback(() => {
    if (!containerRef.current) return;
    const { clientWidth, clientHeight } = containerRef.current;
    const availableWidth = clientWidth || window.innerWidth - 32;
    const minSide = Math.min(availableWidth, clientHeight > 50 ? clientHeight : availableWidth);
    // Limit to reasonable size between 200px and 680px
    const size = Math.max(200, Math.min(680, Math.floor(minSide)));
    setDimension(size);
  }, []);

  useEffect(() => {
    updateDimensions();
    const observer = new ResizeObserver(() => updateDimensions());
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    window.addEventListener('resize', updateDimensions);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateDimensions);
    };
  }, [updateDimensions]);

  // Main Canvas Render Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = dimension * dpr;
    canvas.height = dimension * dpr;
    ctx.scale(dpr, dpr);

    const centerX = dimension / 2;
    const centerY = dimension / 2;
    const outerRadius = dimension / 2 - 14;
    const wheelRadius = outerRadius - 10;
    const centerHubRadius = Math.max(38, wheelRadius * 0.2);

    ctx.clearRect(0, 0, dimension, dimension);

    // 1. Draw Outer Glow and Rim (Static/Ambient)
    ctx.save();
    // Outer drop shadow
    ctx.shadowColor = isSpinning ? 'rgba(99, 102, 241, 0.65)' : 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = isSpinning ? 30 : 20;
    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
    ctx.restore();

    // Metallic Outer Bezel Ring
    const bezelGrad = ctx.createLinearGradient(0, 0, dimension, dimension);
    bezelGrad.addColorStop(0, '#334155');
    bezelGrad.addColorStop(0.3, '#1e293b');
    bezelGrad.addColorStop(0.7, '#475569');
    bezelGrad.addColorStop(1, '#0f172a');

    ctx.beginPath();
    ctx.arc(centerX, centerY, outerRadius, 0, Math.PI * 2);
    ctx.lineWidth = 8;
    ctx.strokeStyle = bezelGrad;
    ctx.stroke();

    // Subtle Neon Rim Line
    ctx.beginPath();
    ctx.arc(centerX, centerY, wheelRadius + 2, 0, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = isSpinning ? '#a855f7' : 'rgba(99, 102, 241, 0.5)';
    ctx.stroke();

    const totalSegments = entries.length;

    // If 0 entries, draw empty placeholder wheel
    if (totalSegments === 0) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(centerX, centerY, wheelRadius, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(30, 41, 59, 0.8)';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 8]);
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.4)';
      ctx.stroke();

      ctx.fillStyle = '#94a3b8';
      ctx.font = `600 16px ${WHEEL_LABEL_FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Add names to start spinning!', centerX, centerY);
      ctx.restore();
      return;
    }

    const segmentAngle = 360 / totalSegments;
    const segmentAngleRad = degToRad(segmentAngle);
    const rotationRad = degToRad(rotationDeg);

    // 2. Draw Wheel Segments with Rotation
    ctx.save();
    ctx.translate(centerX, centerY);
    ctx.rotate(rotationRad);

    for (let i = 0; i < totalSegments; i++) {
      const entry = entries[i];
      const startAngle = i * segmentAngleRad;
      const endAngle = (i + 1) * segmentAngleRad;
      const baseColor = getSegmentColor(i, totalSegments, entry.color, paletteId);

      // Slice Path
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, wheelRadius, startAngle, endAngle);
      ctx.closePath();

      // Segment Radial Gradient for 3D depth
      const sliceGrad = ctx.createRadialGradient(0, 0, centerHubRadius * 0.5, 0, 0, wheelRadius);
      sliceGrad.addColorStop(0, adjustBrightness(baseColor, 15));
      sliceGrad.addColorStop(0.8, baseColor);
      sliceGrad.addColorStop(1, adjustBrightness(baseColor, -18));

      ctx.fillStyle = sliceGrad;
      ctx.fill();

      // Segment Divider Line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
      ctx.lineWidth = totalSegments > 40 ? 1 : 2;
      ctx.stroke();

      // Draw Text Label
      ctx.save();
      const midAngle = startAngle + segmentAngleRad / 2;
      ctx.rotate(midAngle);

      const textColor = getContrastTextColor(baseColor);
      ctx.fillStyle = textColor;

      // Base font size directly from user setting (scaled with canvas dimensions)
      const scaleRatio = dimension / 540;
      const paddingOuter = 16;
      const paddingInner = 12;
      const maxRadialLength = (wheelRadius - paddingOuter) - (centerHubRadius + paddingInner);

      const baseFontSize = (wheelNameFontSize || 24) * scaleRatio;

      // Max allowable font height inside segment wedge near inner region
      const innerTestRadius = centerHubRadius + 24;
      const maxArcHeight = 2 * innerTestRadius * Math.sin(segmentAngleRad / 2) * 0.88;

      let fontSize = baseFontSize;
      if (fontSize > maxArcHeight && maxArcHeight >= 8) {
        fontSize = maxArcHeight;
      }

      let label = entry.label.trim();

      // 1. Try fitting by scaling font size down for longer labels
      ctx.font = `700 ${fontSize}px ${WHEEL_LABEL_FONT}`;
      let measuredWidth = ctx.measureText(label).width;

      if (measuredWidth > maxRadialLength) {
        const requiredFont = fontSize * (maxRadialLength / measuredWidth);
        fontSize = Math.max(8, requiredFont);
        ctx.font = `700 ${fontSize}px ${WHEEL_LABEL_FONT}`;
        measuredWidth = ctx.measureText(label).width;
      }

      // 2. If still exceeding radial length at min font size (8px), truncate with ellipsis
      if (measuredWidth > maxRadialLength) {
        let truncated = label;
        while (truncated.length > 1) {
          truncated = truncated.slice(0, -1);
          const testStr = truncated + '…';
          if (ctx.measureText(testStr).width <= maxRadialLength) {
            label = testStr;
            break;
          }
        }
      }

      ctx.font = `700 ${fontSize}px ${WHEEL_LABEL_FONT}`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';

      // Subtle text drop shadow for legibility
      ctx.shadowColor = textColor === '#ffffff' ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255, 255, 255, 0.35)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;

      // Position text along radius (closer to outer rim)
      const textX = wheelRadius - 16;
      ctx.fillText(label, textX, 0);

      ctx.restore();
    }

    // 3. Draw Outer Tick Pegs (Metallic brass / silver studs at each segment border)
    for (let i = 0; i < totalSegments; i++) {
      const pegAngle = i * segmentAngleRad;
      const pegRadius = wheelRadius - 1;
      const px = Math.cos(pegAngle) * pegRadius;
      const py = Math.sin(pegAngle) * pegRadius;

      ctx.beginPath();
      ctx.arc(px, py, 3.5, 0, Math.PI * 2);
      ctx.fillStyle = '#fbbf24'; // Gold stud
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = '#78350f';
      ctx.stroke();
    }

    ctx.restore(); // Restore from rotation

    // 4. Center Hub (Metallic Bezel & Center Badge)
    ctx.save();
    // Center Hub Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = 15;
    ctx.beginPath();
    ctx.arc(centerX, centerY, centerHubRadius + 4, 0, Math.PI * 2);
    ctx.fillStyle = '#0f172a';
    ctx.fill();
    ctx.restore();

    // Center Metallic Bezel
    const hubGrad = ctx.createLinearGradient(
      centerX - centerHubRadius,
      centerY - centerHubRadius,
      centerX + centerHubRadius,
      centerY + centerHubRadius
    );
    hubGrad.addColorStop(0, '#e2e8f0');
    hubGrad.addColorStop(0.5, '#475569');
    hubGrad.addColorStop(1, '#1e293b');

    ctx.beginPath();
    ctx.arc(centerX, centerY, centerHubRadius, 0, Math.PI * 2);
    ctx.fillStyle = hubGrad;
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#f8fafc';
    ctx.stroke();

    // Center Core (Glossy Button Core)
    const coreRadius = centerHubRadius - 6;
    const coreGrad = ctx.createRadialGradient(
      centerX,
      centerY - 4,
      2,
      centerX,
      centerY,
      coreRadius
    );
    coreGrad.addColorStop(0, isSpinning ? '#ec4899' : '#6366f1');
    coreGrad.addColorStop(1, isSpinning ? '#be185d' : '#4338ca');

    ctx.beginPath();
    ctx.arc(centerX, centerY, coreRadius, 0, Math.PI * 2);
    ctx.fillStyle = coreGrad;
    ctx.fill();

    // Center Label: "SPIN"
    ctx.fillStyle = '#ffffff';
    ctx.font = `800 ${Math.max(12, Math.round(coreRadius * 0.42))}px ${CENTER_BUTTON_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 4;
    ctx.fillText(isSpinning ? '...' : 'SPIN', centerX, centerY);
  }, [entries, rotationDeg, dimension, paletteId, isSpinning, wheelNameFontSize]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '560px',
        aspectRatio: '1 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: `${dimension}px`,
          height: `${dimension}px`,
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'block',
          cursor: disabled ? 'default' : isSpinning ? 'not-allowed' : 'pointer',
        }}
        onClick={() => {
          if (!disabled && !isSpinning && onCenterClick) {
            onCenterClick();
          }
        }}
      />
    </div>
  );
};
